import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  getRepairManagementReport,
  createRepairReportExpense,
  deleteRepairReportExpense
} from '../../services/api';
import { isFullAdmin } from '../../utils/auth';
import { useNavigate } from 'react-router-dom';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportMoney } from '../../utils/tableExport';

const CATEGORY_LABELS = {
  technician_salary: 'Technician Salary',
  building_rental: 'Building Rental',
  electricity: 'Electricity Bill',
  other: 'Other Expenses'
};

const money = (v) =>
  `Rs. ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const today = () => new Date().toISOString().slice(0, 10);

const RepairManagementReport = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(today());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    expense_date: today(),
    category: 'technician_salary',
    description: '',
    amount: '',
    notes: ''
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getRepairManagementReport({ startDate, endDate });
      setReport(res.data);
    } catch (err) {
      setError(err.message || 'Failed to load repair report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!isFullAdmin()) {
      navigate('/dashboard');
      return;
    }
    load();
  }, [load, navigate]);

  const summary = report?.summary;
  const costs = summary?.costs;

  const handleAddExpense = async () => {
    try {
      setError('');
      await createRepairReportExpense({
        ...form,
        amount: Number(form.amount) || 0
      });
      setSuccess('Expense added');
      setDialogOpen(false);
      setForm({
        expense_date: today(),
        category: 'technician_salary',
        description: '',
        amount: '',
        notes: ''
      });
      load();
    } catch (err) {
      setError(err.message || 'Failed to add expense');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await deleteRepairReportExpense(id);
      setSuccess('Expense deleted');
      load();
    } catch (err) {
      setError(err.message || 'Failed to delete expense');
    }
  };

  if (!isFullAdmin()) return null;

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Repair Management Report
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Revenue, job costs, operating expenses, and profit (Admin / Super Admin only)
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={load} disabled={loading}>
                {loading ? 'Loading…' : 'Refresh'}
              </Button>
              <ExportButtons
                title="Repair Management Report"
                filename="repair_management_report"
                subtitle={`Period ${startDate} to ${endDate}`}
                disabled={!summary}
                summary={summary ? [
                  { label: 'Jobs', value: summary.repair_count },
                  { label: 'In-house', value: summary.in_house_count },
                  { label: 'Outsource', value: summary.outsource_count },
                  { label: 'Revenue', value: fmtExportMoney(summary.revenue), emphasis: true },
                  { label: 'Total costs', value: fmtExportMoney(costs?.total_costs) },
                  { label: 'Profit', value: fmtExportMoney(summary.profit), emphasis: true }
                ] : []}
                extraSections={[
                  {
                    title: 'Job Costs',
                    columns: [
                      { label: 'Item', value: (r) => r.label },
                      { label: 'Amount', align: 'right', value: (r) => fmtExportMoney(r.amount) }
                    ],
                    rows: costs ? [
                      { label: 'Parts', amount: costs.parts_cost },
                      { label: 'Labour', amount: costs.labor_cost },
                      { label: 'Service', amount: costs.service_cost },
                      { label: 'Outsource repair charges', amount: costs.outsource_repair_charges },
                      { label: 'Outsource transport', amount: costs.outsource_transport_charges },
                      { label: 'Job costs total', amount: costs.job_costs_total }
                    ] : []
                  },
                  {
                    title: 'Operating Expenses',
                    columns: [
                      { label: 'Category', value: (r) => r.label },
                      { label: 'Amount', align: 'right', value: (r) => fmtExportMoney(r.amount) }
                    ],
                    rows: costs ? [
                      ...Object.entries(CATEGORY_LABELS).map(([key, label]) => ({
                        label,
                        amount: costs.operating_expenses?.[key]
                      })),
                      { label: 'Operating total', amount: costs.operating_expenses_total }
                    ] : []
                  },
                  {
                    title: 'Expense Entries',
                    columns: [
                      { label: 'Date', value: (e) => dash(e.expense_date) },
                      { label: 'Category', value: (e) => CATEGORY_LABELS[e.category] || e.category || '-' },
                      { label: 'Description', value: (e) => dash(e.description) },
                      { label: 'Amount', align: 'right', value: (e) => fmtExportMoney(e.amount) },
                      { label: 'Notes', value: (e) => dash(e.notes) }
                    ],
                    rows: report?.expenses || []
                  }
                ]}
                columns={[]}
                rows={[]}
                onError={setError}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              Add Expense
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {summary && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Card><CardContent>
                <Typography color="text.secondary">Jobs</Typography>
                <Typography variant="h5">{summary.repair_count}</Typography>
                <Typography variant="caption">
                  In-house {summary.in_house_count} · Outsource {summary.outsource_count}
                </Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ bgcolor: 'success.dark', color: '#fff' }}><CardContent>
                <Typography>Revenue</Typography>
                <Typography variant="h5">{money(summary.revenue)}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ bgcolor: 'warning.dark', color: '#fff' }}><CardContent>
                <Typography>Total Costs</Typography>
                <Typography variant="h5">{money(costs?.total_costs)}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ bgcolor: summary.profit >= 0 ? 'primary.dark' : 'error.dark', color: '#fff' }}>
                <CardContent>
                  <Typography>Profit</Typography>
                  <Typography variant="h5">{money(summary.profit)}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Job Costs</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow><TableCell>Parts</TableCell><TableCell align="right">{money(costs.parts_cost)}</TableCell></TableRow>
                    <TableRow><TableCell>Labour</TableCell><TableCell align="right">{money(costs.labor_cost)}</TableCell></TableRow>
                    <TableRow><TableCell>Service</TableCell><TableCell align="right">{money(costs.service_cost)}</TableCell></TableRow>
                    <TableRow><TableCell>Outsource repair charges</TableCell><TableCell align="right">{money(costs.outsource_repair_charges)}</TableCell></TableRow>
                    <TableRow><TableCell>Outsource transport</TableCell><TableCell align="right">{money(costs.outsource_transport_charges)}</TableCell></TableRow>
                    <TableRow>
                      <TableCell><strong>Job costs total</strong></TableCell>
                      <TableCell align="right"><strong>{money(costs.job_costs_total)}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Operating Expenses</Typography>
                <Table size="small">
                  <TableBody>
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                      <TableRow key={key}>
                        <TableCell>{label}</TableCell>
                        <TableCell align="right">{money(costs.operating_expenses?.[key])}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell><strong>Operating total</strong></TableCell>
                      <TableCell align="right"><strong>{money(costs.operating_expenses_total)}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Expense Entries</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell width={60} />
                </TableRow>
              </TableHead>
              <TableBody>
                {(report.expenses || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary">No expenses in this period. Click Add Expense.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  report.expenses.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell>{e.expense_date}</TableCell>
                      <TableCell>{CATEGORY_LABELS[e.category] || e.category}</TableCell>
                      <TableCell>{e.description || '—'}</TableCell>
                      <TableCell align="right">{money(e.amount)}</TableCell>
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => handleDelete(e.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Repair Expense</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                InputLabelProps={{ shrink: true }}
                value={form.expense_date}
                onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Amount"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddExpense}>Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RepairManagementReport;
